import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CheckCircle2,
  Calendar,
  MapPin,
  Eye,
  Edit,
  Trash2,
  Gavel,
  FileText,
  Search,
  Users,
  Shield,
  Clock,
  Briefcase,
  Scale,
  MessageSquare,
  AlertCircle,
  Microscope,
  ScrollText,
  Megaphone,
  Bell,
} from "lucide-react";

/* ─── types ─── */
interface EnhancedTask {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  assigned_to?: string;
  created_at: string;
  case_id?: string;
  task_type?: string;
  category?: string;
  location?: string;
  cases?: { title: string; case_number?: string };
  [key: string]: any;
}

interface Props {
  tasks: EnhancedTask[];
  onEdit: (t: EnhancedTask) => void;
  onDelete: (id: string) => void;
  onView: (t: EnhancedTask) => void;
  onToggleStatus: (t: EnhancedTask) => void;
}

/* ─── group metadata ─── */
const GROUP_META: Record<string, { label: string; color: string; icon: any }> = {
  audiencia:    { label: "Audiências",                  color: "hsl(var(--destructive))",  icon: Gavel },
  peticao:      { label: "Petições",                    color: "hsl(var(--primary))",      icon: FileText },
  diligencia:   { label: "Diligências",                 color: "#f59e0b",                  icon: Search },
  delegacia:    { label: "Acompanhamentos em Delegacia", color: "#6366f1",                 icon: Shield },
  prazo:        { label: "Prazos Processuais",          color: "#ef4444",                  icon: Clock },
  reuniao:      { label: "Reuniões",                    color: "#8b5cf6",                  icon: Users },
  pesquisa:     { label: "Pesquisas Jurídicas",         color: "#06b6d4",                  icon: Microscope },
  protocolo:    { label: "Protocolos",                  color: "#10b981",                  icon: ScrollText },
  recurso:      { label: "Recursos",                    color: "#f97316",                  icon: Megaphone },
  contestacao:  { label: "Contestações",                color: "#ec4899",                  icon: MessageSquare },
  acordo:       { label: "Acordos",                     color: "#14b8a6",                  icon: Scale },
  pericia:      { label: "Perícias",                    color: "#a855f7",                  icon: Microscope },
  cumprimento:  { label: "Cumprimento de Sentença",     color: "#64748b",                  icon: Briefcase },
  sustentacao:  { label: "Sustentações Orais",          color: "#0ea5e9",                  icon: Megaphone },
  despacho:     { label: "Despachos",                   color: "#84cc16",                  icon: ScrollText },
  citacao:      { label: "Citações / Intimações",       color: "#e11d48",                  icon: Bell },
  geral:        { label: "Geral",                       color: "hsl(var(--muted-foreground))", icon: Briefcase },
};

const getGroup = (t: EnhancedTask) => t.task_type || "geral";

/* ─── helpers ─── */
const fmtDate = (iso?: string) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtTime = (iso?: string) => {
  if (!iso) return null;
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  if (h === 12 && m === 0) return null; // noon placeholder = no real time
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

/* ─── component ─── */
export const TaskGroupedList = ({ tasks, onEdit, onDelete, onView, onToggleStatus }: Props) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Group tasks
  const groups: Record<string, EnhancedTask[]> = {};
  tasks.forEach((t) => {
    const g = getGroup(t);
    if (!groups[g]) groups[g] = [];
    groups[g].push(t);
  });

  // Sort groups: audiencia first, then alphabetically
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    if (a === "audiencia") return -1;
    if (b === "audiencia") return 1;
    const la = GROUP_META[a]?.label ?? a;
    const lb = GROUP_META[b]?.label ?? b;
    return la.localeCompare(lb, "pt-BR");
  });

  // Sort tasks inside each group by due_date
  sortedKeys.forEach((k) => {
    groups[k].sort((a, b) => {
      const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
      const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
      return da - db;
    });
  });

  const toggle = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  if (sortedKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <AlertCircle className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-lg font-medium">Nenhuma tarefa encontrada</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedKeys.map((groupKey) => {
        const meta = GROUP_META[groupKey] || GROUP_META.geral;
        const Icon = meta.icon;
        const isCollapsed = collapsed[groupKey];
        const items = groups[groupKey];

        return (
          <div key={groupKey}>
            {/* Group header */}
            <button
              onClick={() => toggle(groupKey)}
              className="flex items-center gap-2 mb-1 px-1 w-full text-left group"
            >
              {isCollapsed ? (
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
              <Icon className="w-5 h-5" style={{ color: meta.color }} />
              <span
                className="text-2xl font-extrabold tracking-tight"
                style={{ color: meta.color }}
              >
                {meta.label}
              </span>
              <Badge
                variant="secondary"
                className="ml-2 text-xs font-semibold rounded-full"
              >
                {items.length}
              </Badge>
            </button>

            {/* Items */}
            {!isCollapsed && (
              <div className="ml-1 border-l-2 pl-4 space-y-0 divide-y divide-border/60"
                style={{ borderColor: meta.color + "40" }}
              >
                {items.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    accentColor={meta.color}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                    onToggleStatus={onToggleStatus}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── single row ─── */
const TaskRow = ({
  task,
  accentColor,
  onEdit,
  onDelete,
  onView,
  onToggleStatus,
}: {
  task: EnhancedTask;
  accentColor: string;
  onEdit: (t: EnhancedTask) => void;
  onDelete: (id: string) => void;
  onView: (t: EnhancedTask) => void;
  onToggleStatus: (t: EnhancedTask) => void;
}) => {
  const done = task.status === "Concluída";
  const overdue =
    !done && task.due_date && new Date(task.due_date) < new Date();

  const date = fmtDate(task.due_date);
  const time = fmtTime(task.due_date);

  return (
    <div className="flex items-start gap-3 py-3 group/row hover:bg-muted/30 transition-colors rounded-lg px-2 -mx-2">
      {/* Check circle */}
      <button
        onClick={() => onToggleStatus(task)}
        className="mt-0.5 shrink-0 transition-transform active:scale-90"
        title={done ? "Reabrir tarefa" : "Concluir tarefa"}
      >
        {done ? (
          <CheckCircle2
            className="w-6 h-6"
            style={{ color: accentColor }}
          />
        ) : (
          <Circle className="w-6 h-6 text-muted-foreground/50 hover:text-muted-foreground" />
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onView(task)}>
        <p
          className={`font-semibold text-[15px] leading-snug truncate ${
            done ? "line-through text-muted-foreground" : "text-foreground"
          }`}
        >
          {task.title}
        </p>

        {/* Subtitle: case info / description */}
        {(task.cases?.title || task.description) && (
          <p className="text-[13px] text-muted-foreground truncate mt-0.5">
            {task.cases?.title
              ? `${task.cases.case_number ? `Processo nº${task.cases.case_number}` : task.cases.title}${task.description ? ` (${task.description})` : ""}`
              : task.description}
          </p>
        )}

        {/* Date & time — bold and prominent */}
        {date && (
          <div className="flex items-center gap-2 mt-1">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span
              className={`text-sm font-bold tabular-nums ${
                overdue ? "text-destructive" : "text-foreground"
              }`}
            >
              {date}
            </span>
            {time && (
              <span
                className={`text-sm font-bold tabular-nums ${
                  overdue ? "text-destructive" : "text-foreground"
                }`}
              >
                {time}
              </span>
            )}
            {overdue && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                Atrasada
              </Badge>
            )}
          </div>
        )}

        {/* Location */}
        {task.location && (
          <div className="flex items-center gap-1.5 mt-0.5 text-[12px] text-muted-foreground">
            <MapPin className="w-3 h-3" />
            <span>{task.location}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity shrink-0">
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onView(task)}>
          <Eye className="w-3.5 h-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(task)}>
          <Edit className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={() => onDelete(task.id)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};